import { Injectable, Type, inject } from '@angular/core';
import { take } from 'rxjs';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';

/**
 * Pass-through data shape similar to legacy `ModalConfig` / `ModalDataType`
 * for definition forms opened via `CustomDialogService`.
 */
export interface CustomDefinitionFormDialogData {
  mode: 'add' | 'edit' | 'view' | string;
  title?: string;
  isReadOnly?: boolean;
  entity?: unknown;
}

export interface CustomDialogOpenOptions<TData = unknown> {
  /** Unique id for tracking / programmatic close (like legacy `modalId`). */
  modalId: string;
  component: Type<unknown>;
  /** Passed to `DynamicDialogConfig.data` (inject `DynamicDialogConfig`). */
  data?: TData;
  /** Passed to `DynamicDialogConfig.inputValues` (maps to the dialog component’s `input()` names). */
  inputValues?: Record<string, unknown>;
  header?: string;
  width?: string;
  height?: string;
  /** PrimeNG dialog host `styleClass` (e.g. bottom sheet layout). */
  styleClass?: string;
  /** PrimeNG dialog content inline styles (e.g. `{ overflow: 'hidden' }`). */
  contentStyle?: Record<string, string>;
  /** PrimeNG dialog position (when supported by the installed version). */
  position?: 'center' | 'top' | 'bottom' | 'left' | 'right' | 'topleft' | 'topright' | 'bottomleft' | 'bottomright';
  /** PrimeNG enter/leave transition timing (e.g. `300ms cubic-bezier(...)`). */
  transitionOptions?: string;
  /**
   * When true: no header close, no mask dismiss, no escape
   * (mapped to PrimeNG `closable`, `dismissableMask`, `closeOnEscape`).
   */
  disableClose?: boolean;
  /**
   * When set, overrides mask-click dismiss (e.g. header close only).
   * Defaults to the inverse of `disableClose` when omitted.
   */
  dismissableMask?: boolean;
  /** When `disableClose` is false, overrides escape behavior. */
  closeOnEscape?: boolean;
  /** Passed to PrimeNG when the same component may already be open. */
  duplicate?: boolean;
  /** PrimeNG overlay base z-index (e.g. above grid column-settings drawer at 11000). */
  baseZIndex?: number;
  focusOnShow?: boolean;
}

/**
 * Wrapper around PrimeNG `DialogService` with a small API similar to
 * legacy `ModalService` (track by `modalId`, `open` / `close`).
 */
@Injectable({ providedIn: 'root' })
export class CustomDialogService {
  private readonly primengDialog = inject(DialogService);
  private readonly refs = new Map<string, DynamicDialogRef>();

  open<TData>(options: CustomDialogOpenOptions<TData>): DynamicDialogRef | null {
    this.close(options.modalId);

    const disableClose = options.disableClose ?? false;
    const dismissableMask = options.dismissableMask ?? !disableClose;

    const ref = this.primengDialog.open(options.component, {
      header: options.header ?? '',
      width: options.width ?? '50vw',
      height: options.height,
      modal: true,
      ...(options.data !== undefined ? { data: options.data } : {}),
      ...(options.inputValues ? { inputValues: options.inputValues } : {}),
      ...(options.styleClass ? { styleClass: options.styleClass } : {}),
      ...(options.contentStyle ? { contentStyle: options.contentStyle } : {}),
      ...(options.position ? { position: options.position } : {}),
      ...(options.transitionOptions ? { transitionOptions: options.transitionOptions } : {}),
      closable: !disableClose,
      dismissableMask,
      closeOnEscape: options.closeOnEscape ?? !disableClose,
      duplicate: options.duplicate ?? false,
      ...(options.baseZIndex != null ? { baseZIndex: options.baseZIndex } : {}),
      ...(options.focusOnShow != null ? { focusOnShow: options.focusOnShow } : {}),
    });

    if (!ref) {
      return null;
    }

    this.refs.set(options.modalId, ref);
    ref.onClose.pipe(take(1)).subscribe(() => {
      this.refs.delete(options.modalId);
    });

    return ref;
  }

  close(modalId: string, result?: unknown): void {
    this.refs.get(modalId)?.close(result);
  }

  closeAll(): void {
    for (const id of [...this.refs.keys()]) {
      this.close(id);
    }
  }
}
